import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { AppContextProvider } from "./AppContextProvider";
import { ApiHelperAppContextProvider } from "./common/infrastructure/appContexts/ApiHelperAppContextProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "react-hot-toast";
import { AppConfirmDialog } from "./common/components/dialogs/AppConfirmDialog";
import AuthProvider from "react-auth-kit";
import createStore from "react-auth-kit/createStore";
import { PrimeReactProvider } from "primereact/api";
import { ErrorBoundary } from "./common/components/errorPages/ErrorBoundary";
import { StudioNotFound } from "./common/components/errorPages/StudioNotFound";
import { routePaths } from "./common/infrastructure/routes/routePaths";
import { FeatureFlagsProvider } from "./common/infrastructure/appContexts/FeatureFlagsProvider";
import { confirmOpenedPushFromLocation } from "./common/infrastructure/utilities/notificationClickUtility";
import { removeBootLoader } from "./common/components/loading/bootLoaderUtility";

const root = ReactDOM.createRoot(document.getElementById("root")!);

// The domain does not belong to an active studio (unknown, or the studio was deleted) — the
// API answers 451 and axiosHelper sends the visitor here. Checked before initializing the app
if (window.location.pathname === routePaths.invalidDomain) {
  // Render minimal error page without any app context or providers. Nothing loads after it, so
  // index.html's boot loader goes now (ApiHelperAppContextProvider removes it everywhere else).
  removeBootLoader();
  root.render(
    <React.StrictMode>
      <StudioNotFound />
    </React.StrictMode>
  );
} else {
  // Normal app initialization
  // Before the router reads the address: an iPhone opens a notification's link with a
  // delivery token, which is confirmed and taken out here.
  confirmOpenedPushFromLocation();

  const queryClient = new QueryClient();

  const store = createStore({
    authName: "_auth",
    authType: "cookie",
    cookieDomain: window.location.hostname,
    cookieSecure: window.location.protocol === "https:",
  });

  const value = {
    hideOverlaysOnDocumentScrolling: false,
  };

  root.render(
    <BrowserRouter>
      <React.StrictMode>
        <AuthProvider store={store}>
          <QueryClientProvider client={queryClient}>
            <ApiHelperAppContextProvider>
              <PrimeReactProvider value={value}>
                <AppContextProvider>
                  <FeatureFlagsProvider>
                    <ErrorBoundary>
                      <App />
                      <ReactQueryDevtools initialIsOpen={false} />
                      <Toaster />
                      <AppConfirmDialog />
                    </ErrorBoundary>
                  </FeatureFlagsProvider>
                </AppContextProvider>
              </PrimeReactProvider>
            </ApiHelperAppContextProvider>
          </QueryClientProvider>
        </AuthProvider>
      </React.StrictMode>
    </BrowserRouter>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
